import * as R from "ramda";
import * as React from "react";
import { FaRegArrowAltCircleDown } from "react-icons/fa";
import styled from "styled-components";

import { FooterNote, FormSelect, SettingsInputItem } from "../../../../App/Common/Components/FormComponents";
import { Section, SectionTitle } from "../../../../App/Common/Styles/containers";
import { config } from "../../../../config";
import { fullCourseViews } from "../../../../utils/courseViews";
import { LocaleNames, PreferredLocale } from "../../../../utils/locales";
import { ImportTranslations } from "../../../course-author/tabs/settings/ImportTranslations.container";
import { ICourseEditOperations, IFullCourse } from "../../../course-author/types";
import ClickableElement from "../../../utils/ClickableElement";
import CheckBox from "../Checkbox";

interface ILocaleOptionProps {
  locale: PreferredLocale;
}

const LocaleOption: React.SFC<ILocaleOptionProps> = props => (
  <option value={props.locale}>{LocaleNames[props.locale]}</option>
);

const DownloadLink = styled.a`
  color: inherit;
  padding: 1rem;
  text-decoration: none;
  width: auto;
`;

interface IDownloadLocalizationProps {
  locale: PreferredLocale;
  courseId: string;
  userId: string;
}

const DownloadLocalization: React.SFC<IDownloadLocalizationProps & React.HTMLProps<HTMLButtonElement>> = props => (
  <DownloadLink
    href={`${config.saltcomplianceApiRoot}/courses/${props.courseId}/_localization/${props.locale}/translations.csv?activeUser=${props.userId}`}
  >
    <ClickableElement>
      <FaRegArrowAltCircleDown color={props.checked ? "green" : "black"} />
    </ClickableElement>
  </DownloadLink>
);

interface IAdditionalLocaleProps {
  courseId: string;
  locale: PreferredLocale;
  supportedLocales: PreferredLocale[];
  userId: string;
}

const SectionSubTitle = styled.h4`
  margin: 2rem 0 2rem 0;
  font-weight: 600;
`;

const AdditionalLocaleDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;

  label {
    align-items: flex-start;
    display: flex;
    justify-content: flex-start;
    width: 17.5rem;
  }
`;

const AdditionalLocalesContainer = styled(SettingsInputItem)`
  flex-wrap: wrap;

  input {
    margin-left: 0;
  }

  span {
    margin-left: 1rem;
  }
`;

const AdditionalLocale: React.SFC<IAdditionalLocaleProps & React.HTMLProps<HTMLInputElement>> = props => (
  <AdditionalLocaleDiv>
    <CheckBox onChange={props.onChange} defaultChecked={R.contains(props.locale, props.supportedLocales)}>
      <span>{LocaleNames[props.locale]}</span>
    </CheckBox>
    <DownloadLocalization
      courseId={props.courseId}
      locale={props.locale}
      userId={props.userId}
      checked={R.contains(props.locale, props.supportedLocales)}
    />
  </AdditionalLocaleDiv>
);

interface IAdditionalLocalesProps {
  courseId: string;
  masterLocale: PreferredLocale;
  supportedLocales: PreferredLocale[];
  toggleSupportedLocale: (locale: PreferredLocale) => () => void;
  userId: string;
}

const AdditionalLocales: React.SFC<IAdditionalLocalesProps> = props => (
  <AdditionalLocalesContainer>
    {R.compose<PreferredLocale[], PreferredLocale[], JSX.Element[]>(
      R.map<PreferredLocale, JSX.Element>(locale => (
        <AdditionalLocale
          courseId={props.courseId}
          onChange={props.toggleSupportedLocale(locale)}
          key={locale}
          locale={locale}
          supportedLocales={props.supportedLocales}
          userId={props.userId}
        />
      )),
      R.filter((locale: PreferredLocale) => locale !== props.masterLocale)
    )(Object.keys(LocaleNames) as PreferredLocale[])}
  </AdditionalLocalesContainer>
);

export interface ISelectLocalizationsProps {
  course: IFullCourse;
  courseId: string;
  editOperations: ICourseEditOperations;
  userId: string;
}

export interface ISelectLocalizationsState {
  masterLocale: PreferredLocale;
  supportedLocales: PreferredLocale[];
}

export default class SelectLocalizations extends React.Component<ISelectLocalizationsProps, ISelectLocalizationsState> {
  private selectRef: React.RefObject<HTMLSelectElement>;

  constructor(props: ISelectLocalizationsProps) {
    super(props);
    this.state = {
      masterLocale: fullCourseViews.masterLocale(props.course) || PreferredLocale.en,
      supportedLocales: fullCourseViews.supportedLocales(props.course) || [],
    };
    this.selectRef = React.createRef();
  }

  public selectMasterLocale = () => {
    if (R.isNil(this.selectRef.current)) {
      return;
    }

    const value = this.selectRef.current.value as PreferredLocale;
    if (value !== this.state.masterLocale) {
      this.props.editOperations.modifyMasterLocale(value);
      this.setState({ masterLocale: value });
    }
  };

  public toggleSupportedLocale = (locale: PreferredLocale) => () => {
    const supportedLocales = fullCourseViews.supportedLocales(this.props.course) || [];

    if (R.contains(locale, supportedLocales)) {
      this.props.editOperations.deleteElement(locale, "localeSettings.supportedLocales");
      this.setState({
        supportedLocales: R.filter(loc => loc !== locale, this.state.supportedLocales),
      });
    } else {
      this.props.editOperations.insertElement(locale, "localeSettings.supportedLocales");
      this.setState({
        supportedLocales: [...this.state.supportedLocales, locale],
      });
    }
  };

  public render() {
    const allLocales = Object.keys(LocaleNames) as PreferredLocale[];

    return (
      <Section>
        <SectionTitle>Course Translations</SectionTitle>
        <label htmlFor="master-localization-select">
          <SectionSubTitle>Master language</SectionSubTitle>
        </label>
        <SettingsInputItem>
          <FormSelect
            ref={this.selectRef}
            name="master-localization-select"
            onChange={this.selectMasterLocale}
            value={this.state.masterLocale}
          >
            {R.map(
              locale => (
                <LocaleOption key={locale} locale={locale} />
              ),
              allLocales
            )}
          </FormSelect>
        </SettingsInputItem>
        <FooterNote>The language specified above is the first language presented to the learners.</FooterNote>
        <SectionSubTitle>Additional languages</SectionSubTitle>
        <AdditionalLocales
          courseId={this.props.courseId}
          masterLocale={this.state.masterLocale}
          supportedLocales={this.state.supportedLocales}
          toggleSupportedLocale={this.toggleSupportedLocale}
          userId={this.props.userId}
        />
        <FooterNote>
          To download a language pack CSV for translating, select the download icon next to the language. To enable a
          language, select the checkbox next to it. The language will only present if a translated CSV has been
          uploaded.
        </FooterNote>
        <ImportTranslations />
      </Section>
    );
  }
}
